// Step 1 — pick a truck preset or enter custom interior dimensions + max
// payload. Presets come from truckPacker.js so this list stays in sync with
// whatever the rest of the warehouse module knows about.

import React from 'react';
import { Row, Col, Form, Card, Badge } from 'react-bootstrap';

import { TRUCK_PRESETS } from '../../../features/warehouse/truckPacker.js';

const CUSTOM_KEY = '__custom';

export default function StepTruck({ truck, onChange }) {
  const isCustom = !Object.values(TRUCK_PRESETS).some(p => p.id === truck.id);

  const selectPreset = presetId => {
    if (presetId === CUSTOM_KEY) {
      onChange({
        id: CUSTOM_KEY,
        label: 'Custom',
        lengthM: truck.lengthM,
        widthM: truck.widthM,
        heightM: truck.heightM,
        maxPayloadKg: truck.maxPayloadKg,
      });
      return;
    }
    const p = TRUCK_PRESETS[presetId];
    onChange({ ...p, maxPayloadKg: truck.maxPayloadKg });
  };

  const setField = (key, raw) => {
    const n = Number(raw);
    onChange({ ...truck, [key]: Number.isFinite(n) ? n : 0 });
  };

  const floorAreaM2 = (Number(truck.lengthM) || 0) * (Number(truck.widthM) || 0);
  const volumeM3 = floorAreaM2 * (Number(truck.heightM) || 0);

  return (
    <Row>
      <Col md={7}>
        <Card className="mb-3">
          <Card.Header className="fw-semibold">Truck</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={7}>
                <Form.Label>Preset</Form.Label>
                <Form.Select
                  value={isCustom ? CUSTOM_KEY : truck.id}
                  onChange={e => selectPreset(e.target.value)}
                >
                  {Object.values(TRUCK_PRESETS).map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                  <option value={CUSTOM_KEY}>Custom…</option>
                </Form.Select>
              </Col>
              <Col md={5}>
                <Form.Label>Max payload (kg)</Form.Label>
                <Form.Control
                  type="number" min={0} step={100}
                  value={truck.maxPayloadKg || ''}
                  onChange={e => setField('maxPayloadKg', e.target.value)}
                />
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Label>Length (m)</Form.Label>
                <Form.Control
                  type="number" min={0} step={0.1}
                  value={truck.lengthM || ''}
                  onChange={e => setField('lengthM', e.target.value)}
                  disabled={!isCustom}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Width (m)</Form.Label>
                <Form.Control
                  type="number" min={0} step={0.01}
                  value={truck.widthM || ''}
                  onChange={e => setField('widthM', e.target.value)}
                  disabled={!isCustom}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Height (m)</Form.Label>
                <Form.Control
                  type="number" min={0} step={0.01}
                  value={truck.heightM || ''}
                  onChange={e => setField('heightM', e.target.value)}
                  disabled={!isCustom}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      <Col md={5}>
        <Card className="mb-3 bg-body-tertiary">
          <Card.Header className="fw-semibold">Preview</Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Name</span>
              <strong>{truck.label || '—'}</strong>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Interior</span>
              <strong>{truck.lengthM} × {truck.widthM} × {truck.heightM} m</strong>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Floor area</span>
              <strong>{floorAreaM2.toFixed(2)} m²</strong>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Volume</span>
              <strong>{volumeM3.toFixed(2)} m³</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Max payload</span>
              <Badge bg={truck.maxPayloadKg ? 'primary' : 'secondary'}>
                {truck.maxPayloadKg ? `${truck.maxPayloadKg} kg` : 'unset'}
              </Badge>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}
